import { useFetch } from '@/composables/useFetch';
import router from '@/router';
import type { AiRequest, DishGenerationRequest, DishTag } from '@/types/api';
import type { Dish, Ingredient, Recipe, RecipeStep, Unit } from '@/types/dish';
import { normalizeQuantityAndUnit, standardizeUnitName } from '@/utils/dishes';
import { generateImage } from '@/utils/images';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type { ToastServiceMethods } from 'primevue/toastservice';
import { useToast } from 'primevue/usetoast';
import { ref, type Ref } from 'vue';
import { useEnvStore } from './envStore';
import { useUserStore } from './useUserStore';
// import { useI18n } from 'vue-i18n'
import i18n from '@/i18n';

import { usePlatform } from '@/composables/usePlatform';
import { useProfileDishLists } from '@/composables/useProfileDishLists';
import { useOrderList } from './orderStore';
import { usePlanningStore } from './planningStore';

// export const useDishesStore = defineStore('dishes', {
export const useDishesStore = defineStore('dishes', () => {
  const { isNative } = usePlatform(); // Get native check
  const dishes: Ref<Dish[]> = ref([]);
  const tags: Ref<DishTag[] | []> = ref([]);
  const dishTitles: Ref<string[]> = ref([]);
  const otherDishTitles: Ref<string[]> = ref([]);
  const otherDishTitlesFromIngredients: Ref<string[]> = ref([]);
  const infoTotal: Ref<Record<string, unknown> | null> = ref(null);
  const selectedDish: Ref<Dish | null> = ref(null);
  const units: Ref<Unit[]> = ref([]);
  const { fetchData } = useFetch();
  const envStore = useEnvStore();
  // const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' })
  const toast = useToast();
  const isGenerating = ref(false);
  const generatingImage = ref(false);
  const shouldRefetch = ref<boolean>(false);
  const showingDishFromPlan = ref<boolean>(false);

  async function addIngredients(dishId: number, newIngredients: Ingredient[]) {
    if (!selectedDish.value || selectedDish.value.id !== dishId) return;
    const servings = selectedDish.value.servings;

    newIngredients = newIngredients.map((ingredient) => {
      const price = ingredient?.price || 0;
      return {
        ...ingredient,
        price: price,
      };
    });

    // normalize data to optimistically update the dish
    const normalizedIngredients = newIngredients.map((ingredient) => {
      const price = ingredient?.price || 0;
      const standardizedUnitName = standardizeUnitName(ingredient.unit);
      let normalizedQuantity = ingredient?.quantity || 0;
      let normalizedUnit = ingredient.unit;
      [normalizedQuantity, normalizedUnit] = normalizeQuantityAndUnit(normalizedQuantity, standardizedUnitName);
      return {
        ...ingredient,
        quantity: normalizedQuantity / servings,
        unit: normalizedUnit,
        price: price / servings,
      };
    });

    // Optimistic UI Update
    const originalIngredients = [...selectedDish.value.ingredients];

    // Optimistic UI Update
    selectedDish.value.ingredients.push(...normalizedIngredients);

    const payload = { ingredients: newIngredients, servings: selectedDish.value.servings };
    try {
      const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/ingredients`, 'POST', payload);
      if (error) throw error;

      // Update the new ingredients with the IDs returned from the server
      selectedDish.value.ingredients = [...originalIngredients, ...data.ingredients];
      selectedDish.value.needs_recipe_update = true;
      // data.ingredients.forEach((serverIngredient: Ingredient, index: number) => {
      //   if (!selectedDish.value) return
      //   const newIngredientIndex = selectedDish.value.ingredients.findIndex(
      //     (ing) =>
      //       ing.name === newIngredients[index].name &&
      //       ing.quantity === newIngredients[index].quantity &&
      //       ing.unit === newIngredients[index].unit &&
      //       ing.category === newIngredients[index].category
      //   )
      //   if (newIngredientIndex !== -1) {
      //     selectedDish.value.ingredients[newIngredientIndex] = serverIngredient
      //   }
      // })

      // toast.add({
      //   severity: 'success',
      //   summary: i18n.global.t('common.success'),
      //   detail: i18n.global.t('menuDishView.ingredients.addIngredientSuccess'),
      //   life: 3000,
      // })
    } catch (error) {
      console.error('Failed to add ingredients', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuDishView.ingredients.addIngredientError'),
        life: 3000,
      });
      // Rollback to previous state
      selectedDish.value.ingredients = originalIngredients;
    }
  }

  async function generateDishFromTitle(title: string, restaurantId: string, dishId?: number): Promise<Dish | null> {
    // console.log("generate Dish from title");
    // console.log(dishId)

    // Check if the user can still generation dishes
    if (!useUserStore().validateUserCanGenerate()) {
      return null;
    }
    try {
      const params = {
        custom_title: title,
        generation_type: 'from_title',
        restaurant_id: restaurantId,
        dish_title: title,
        prompt_language: 'fr',
        dish_title_id: dishId ?? null, // TODO Language
      };
      const { data, error } = await fetchData(`${envStore.apiUrl}/generate/dish-from-title`, 'POST', params);
      if (error) {
        if ((error as { code: string }).code === 'INSUFFICIENT_CREDITS') {
          toast.add({
            severity: 'error',
            summary: i18n.global.t('credits.noMoreCredit.title'),
            detail: i18n.global.t('credits.noMoreCredit.message'),
            life: 3000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.global.t('common.error'),
            detail: i18n.global.t('creationFilters.generationError'),
            life: 3000,
          });
        }
        return null;
      }

      return data.response; // Retourne le plat généré
    } catch (error) {
      console.error('Error in store generateDishFromTitle:', error);
      return null;
    }
  }

  async function generateMenuDish(
    dishName: string,
    categoryName: string,
    restaurantId: string,
    dishDescription?: string,
    menuDishId?: number,
  ): Promise<{ dish: Dish | null; updatedMenuDish?: object }> {
    // Check if the user can still generate dishes
    if (!useUserStore().validateUserCanGenerate()) {
      return { dish: null };
    }

    try {
      const params = {
        dish_name: dishName,
        category_name: categoryName,
        dish_description: dishDescription || '',
        restaurant_id: restaurantId,
        prompt_language: 'fr', // TODO: Use dynamic language
        ...(menuDishId && { menu_dish_id: menuDishId }),
      };

      const { data, error } = await fetchData(`${envStore.apiUrl}/generate/menu-dish`, 'POST', params);

      if (error) {
        if ((error as { code: string }).code === 'INSUFFICIENT_CREDITS') {
          toast.add({
            severity: 'error',
            summary: i18n.global.t('credits.noMoreCredit.title'),
            detail: i18n.global.t('credits.noMoreCredit.message'),
            life: 3000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.global.t('common.error'),
            detail: i18n.global.t('creationFilters.generationError'),
            life: 3000,
          });
        }
        return { dish: null };
      }

      // Return the first dish if dishes were generated
      // The response structure is: { response: { dishes: [dish1, dish2, ...] }, updated_menu_dish?: MenuDish }
      const dish = data.response && data.response.dishes && data.response.dishes.length > 0 ? data.response.dishes[0] : null;

      return {
        dish,
        updatedMenuDish: data.updated_menu_dish,
      };
    } catch (error) {
      console.error('Error in store generateMenuDish:', error);
      return { dish: null };
    }
  }

  // Function to update ingredients in a dish
  async function updateIngredients(dishId: number, updatedIngredients: Ingredient[]) {
    if (!selectedDish.value) return;
    const servings = selectedDish.value.servings;

    updatedIngredients = updatedIngredients.map((ingredient) => {
      const price = ingredient?.price || 0;
      return {
        ...ingredient,
        price: price,
      };
    });

    // normalize data to optimistically update the dish
    const normalizedIngredients = updatedIngredients.map((ingredient) => {
      const standardizedUnitName = standardizeUnitName(ingredient.unit);
      const price = ingredient?.price || 0;
      let normalizedQuantity = ingredient?.quantity || 0;
      let normalizedUnit = ingredient.unit;
      [normalizedQuantity, normalizedUnit] = normalizeQuantityAndUnit(normalizedQuantity, standardizedUnitName);
      return {
        ...ingredient,
        quantity: normalizedQuantity / servings,
        unit: normalizedUnit,
        price: price / servings,
      };
    });

    // Store original state for possible rollback
    const oldIngredients = selectedDish.value.ingredients.map((ing) => ({ ...ing }));

    // Optimistic UI Update
    normalizedIngredients.forEach((updatedIngredient) => {
      if (!selectedDish.value) return;
      const index = selectedDish.value.ingredients.findIndex((ing) => ing.id === updatedIngredient.id);
      if (index !== -1) {
        selectedDish.value.ingredients[index] = {
          ...selectedDish.value.ingredients[index],
          ...updatedIngredient,
        };
      }
    });

    const payload = { ingredients: updatedIngredients, servings: selectedDish.value.servings };

    try {
      const { error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/ingredients`, 'PATCH', payload);
      if (error) throw error;

      selectedDish.value.needs_recipe_update = true;
      useOrderList().shouldRefresh = true;
      // Update could return new data to be reconciled
      // selectedDish.value.ingredients = data;
      // toast.add({
      //   severity: 'success',
      //   summary: i18n.global.t('common.success'),
      //   detail: i18n.global.t('menuDishView.ingredients.updateIngredientSuccess'),
      //   life: 3000,
      // })
    } catch (error) {
      console.error('Failed to update ingredients', error);
      // Rollback
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuDishView.ingredients.updateIngredientError'),
        life: 3000,
      });
      selectedDish.value.ingredients = oldIngredients;
    }
  }

  // Function to delete ingredients from a dish
  async function deleteIngredients(dishId: number, ingredientIds: number[]) {
    if (!selectedDish.value) return;
    const originalIngredients = selectedDish.value.ingredients.filter((ing) => ingredientIds.includes(ing.id));
    // Optimistic UI Update
    selectedDish.value.ingredients = selectedDish.value.ingredients.filter((ing) => !ingredientIds.includes(ing.id));

    try {
      const { error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/ingredients`, 'DELETE', { ingredientIds });
      if (error) throw error;
      selectedDish.value.needs_recipe_update = true;
      toast.add({
        severity: 'success',
        summary: i18n.global.t('common.success'),
        detail: i18n.global.t('menuDishView.ingredients.deleteIngredientSuccess'),
        life: 3000,
      });
      useOrderList().shouldRefresh = true;
    } catch (error) {
      console.error('Failed to delete ingredients', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuDishView.ingredients.deleteIngredientError'),
        life: 3000,
      });
      // Rollback
      selectedDish.value.ingredients.push(...originalIngredients);
    }
  }

  const defaultGenerationParams: DishGenerationRequest = {
    restaurant_id: '',
    prompt_language: 'en',
    main_product: '',
    custom_title: '',
    max_price: 6,
    sweet_salt: '',
    dish_type: 'main',
    hot_cold: [],
    dish_inspirations: [],
    dish_diet: [],
    dish_season: [],
    dish_product_preferences: [],
    supp_ingredients: [],
    supp_prompt: '',
    generation_type: 'from_title',
    ai_model: 'gpt-4-turbo',
    ai_temperature: 0.6,
    nutrients: {},
    allergens: [],
    excluded_ingredients: [] as string[],
    img_product: undefined,
  };

  const genParamsInspirationRegion: Ref<string[]> = ref([]);
  const genParamsInspirationCountry: Ref<string[]> = ref([]);

  const inspirationSource: Ref<'none' | 'region' | 'country'> = ref('none'); // none, 'region', or 'country'

  const generationParams: Ref<DishGenerationRequest> = ref({
    restaurant_id: '',
    prompt_language: 'en',
    main_product: '',
    custom_title: '',
    max_price: 6,
    sweet_salt: '',
    dish_type: 'main',
    hot_cold: [],
    dish_inspirations: [],
    dish_diet: [],
    dish_season: [],
    dish_product_preferences: [],
    supp_ingredients: [],
    supp_prompt: '',
    generation_type: 'from_title',
    ai_model: 'gpt-4-turbo',
    ai_temperature: 0.6,
    nutrients: {},
    allergens: [],
    excluded_ingredients: [] as string[],
  });

  // To keep the last generation parameters, and reset the generationParams of the menuFiltersView.
  const lastGenerationParams: Ref<DishGenerationRequest> = ref({
    restaurant_id: '',
    prompt_language: 'en',
    main_product: '',
    custom_title: '',
    max_price: 6,
    sweet_salt: '',
    dish_type: 'main',
    hot_cold: [],
    dish_inspirations: [],
    dish_diet: [],
    dish_season: [],
    dish_product_preferences: [],
    supp_ingredients: [],
    supp_prompt: '',
    generation_type: 'from_title',
    ai_model: 'gpt-4-turbo',
    ai_temperature: 0.6,
    nutrients: {},
    allergens: [],
    excluded_ingredients: [] as string[],
  });

  function resetGenerationParams() {
    let restaurantId = '';
    const generationType = generationParams.value.generation_type;
    if (generationParams.value.restaurant_id) {
      restaurantId = generationParams.value.restaurant_id;
    }
    genParamsInspirationRegion.value = [];
    genParamsInspirationCountry.value = [];
    // console.log('defaultGenerationara', defaultGenerationParams);
    generationParams.value = JSON.parse(JSON.stringify(defaultGenerationParams));
    generationParams.value.restaurant_id = restaurantId;
    generationParams.value.generation_type = generationType;
    inspirationSource.value = 'none';
  }

  function cleanGenerationParams() {
    const img_product = generationParams.value.img_product;
    let cleanParams = JSON.parse(JSON.stringify(defaultGenerationParams));
    generationParams.value.dish_inspirations = [...genParamsInspirationRegion.value, ...genParamsInspirationCountry.value];
    // console.log('cleanparams : ', cleanParams);
    // console.log('generation params', generationParams.value);
    cleanParams = {
      ...cleanParams,
      generation_type: generationParams.value.generation_type,
      restaurant_id: generationParams.value.restaurant_id,
      prompt_language: generationParams.value.prompt_language,
      max_price: generationParams.value.max_price,
      hot_cold: generationParams.value.hot_cold,
      sweet_salt: generationParams.value.sweet_salt,
      dish_inspirations: generationParams.value.dish_inspirations,
      dish_diet: generationParams.value.dish_diet,
      dish_season: generationParams.value.dish_season,
      dish_product_preferences: generationParams.value.dish_product_preferences,
      supp_prompt: generationParams.value.supp_prompt,
      supp_ingredients: generationParams.value.supp_ingredients,
      ai_model: generationParams.value.ai_model,
      ai_temperature: generationParams.value.ai_temperature,
      nutrients: generationParams.value.nutrients,
      allergens: generationParams.value.allergens ? generationParams.value.allergens : [],
      excluded_ingredients: generationParams.value.excluded_ingredients ? generationParams.value.excluded_ingredients : [],
    };
    switch (generationParams.value.generation_type) {
      case 'quick':
        cleanParams = {
          ...cleanParams,
          dish_type: generationParams.value.dish_type,
        };
        genParamsInspirationCountry.value = [];
        genParamsInspirationRegion.value = [];
        break;
      case 'with_main_product':
        cleanParams = {
          ...cleanParams,
          main_product: generationParams.value.main_product,
          img_product: img_product,
        };
        break;
      case 'from_title':
        cleanParams = {
          ...cleanParams,
          custom_title: generationParams.value.custom_title,
        };
        break;
      default:
        break;
    }
    // console.log('updated cleanParams : ', cleanParams);
    generationParams.value = JSON.parse(JSON.stringify(cleanParams));
    generationParams.value.img_product = img_product;
    console.log('generationParams after clean : ', generationParams.value);
    // loop on each param
    for (const [key, value] of Object.entries(generationParams.value)) {
      console.log(`Param: ${key}, Value: ${value}`);
    }
  }

  function getName(input: string | File | undefined): string {
    if (typeof input === 'string') return input;
    if (input instanceof File) return input.name;
    return '';
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  async function generateDishes(params: DishGenerationRequest) {
    const userStore = useUserStore();

    if (!userStore.user) {
      console.error('No user found');
      return;
    }

    if (!userStore.validateUserCanGenerate()) {
      return null;
    }

    const restaurantId = userStore.user.restaurants[0].id;
    let tempId: number | null = null;

    isGenerating.value = true;
    if (dishes.value.length > 0) {
      // empty the array
      dishes.value.splice(0, dishes.value.length);
    }
    if (dishTitles.value.length > 0) {
      // empty the array
      dishTitles.value.splice(0, dishTitles.value.length);
    }
    otherDishTitles.value = [];
    otherDishTitlesFromIngredients.value = [];

    if (params.generation_type === 'from_title') {
      tempId = Date.now();
      const placeholderDish: Dish = {
        id: tempId,
        name: getName(params.main_product) || params.custom_title || params.dish_title || '',
        ingredients: [],
        servings: 0,
        difficulty: 1,
        hasRecipe: false,
        loading: true,
        restaurant_id: null,
        author: null,
        created_at: '',
        updated_at: '',
        ai_request_id: 0,
        prep_time_min: 0,
        created_using: 'ia',
        chill_time_min: 0,
        cook_time_min: 0,
        is_favorite: false,
        selected_image: 'gastro',
        is_template: false,
        country_alpha2_code: 'CH',
      };
      dishes.value.push(placeholderDish);
    }

    generationParams.value.restaurant_id = restaurantId;
    lastGenerationParams.value.restaurant_id = restaurantId;

    let urlEndpoint = useEnvStore().apiUrl + '/generate/dishes';
    if (params.generation_type === 'from_title') {
      urlEndpoint = useEnvStore().apiUrl + '/generate/dishes/from_title';
    } else if (params.generation_type === 'with_main_product') {
      urlEndpoint = useEnvStore().apiUrl + '/generate/dishes/with_main_product';
    } else if (params.generation_type === 'quick') {
      urlEndpoint = useEnvStore().apiUrl + '/generate/dishes/quick';
    }

    let payload: FormData | DishGenerationRequest | Record<string, unknown>; // Update type definition

    console.log('Generation type : ', params.generation_type);
    if (params.generation_type === 'with_main_product' && params.img_product instanceof File) {
      if (isNative.value) {
        // --- NATIVE: Manual JSON Construction with Base64 ---
        const base64String = await fileToBase64(params.img_product);

        // Construct a plain JSON object matching DishGenerationRequest
        payload = {
          ...params,
          restaurant_id: params.restaurant_id || restaurantId,
          // Send as base64_image so Laravel's prepareForValidation catches it
          base64_image: base64String,
          // Remove the raw file object to avoid serialization errors
          img_product: undefined,
        };

        console.log('Native Payload prepared:', payload);
      } else {
        // --- WEB: Standard FormData ---
        const formData = new FormData();
        formData.append('img_product', params.img_product);

        // Add all required fields to FormData
        formData.append('generation_type', params.generation_type);
        formData.append('restaurant_id', params.restaurant_id || restaurantId);
        formData.append('prompt_language', params.prompt_language);
        formData.append('max_price', params.max_price.toString());
        formData.append('ai_model', params.ai_model);

        // Add optional main_product if it exists
        if (params.main_product) {
          formData.append('main_product', params.main_product);
        }

        // Add optional fields
        if (params.sweet_salt) formData.append('sweet_salt', params.sweet_salt);
        if (params.hot_cold && params.hot_cold.length > 0) {
          formData.append('hot_cold', JSON.stringify(params.hot_cold));
        }
        if (params.dish_inspirations && params.dish_inspirations.length > 0) {
          formData.append('dish_inspirations', JSON.stringify(params.dish_inspirations));
        }
        if (params.dish_diet && params.dish_diet.length > 0) {
          formData.append('dish_diet', JSON.stringify(params.dish_diet));
        }
        if (params.dish_season && params.dish_season.length > 0) {
          formData.append('dish_season', JSON.stringify(params.dish_season));
        }
        if (params.supp_prompt) formData.append('supp_prompt', params.supp_prompt);
        if (params.supp_ingredients && params.supp_ingredients.length > 0) {
          formData.append('supp_ingredients', JSON.stringify(params.supp_ingredients));
        }
        if (params.ai_temperature !== undefined) {
          formData.append('ai_temperature', params.ai_temperature.toString());
        }
        if (params.allergens && params.allergens.length > 0) {
          formData.append('allergens', JSON.stringify(params.allergens));
        }
        if (params.excluded_ingredients && params.excluded_ingredients.length > 0) {
          formData.append('excluded_ingredients', JSON.stringify(params.excluded_ingredients));
        }
        if (params.nutrients && Object.keys(params.nutrients).length > 0) {
          formData.append('nutrients', JSON.stringify(params.nutrients));
        }

        payload = formData;
        console.log('Payload as FormData for image upload:', payload);
        // loop on each formData entry
        for (const pair of formData.entries()) {
          console.log(`FormData Param: ${pair[0]}, Value: ${pair[1]}`);
        }
      }
    } else {
      // For non-file requests, use the params object directly
      payload = { ...params, restaurant_id: params.restaurant_id || restaurantId };
      console.log('Payload as JSON:', payload);
      // loop on each param
      for (const [key, value] of Object.entries(payload)) {
        console.log(`Param: ${key}, Value: ${value}`);
      }
    }

    const { data, error } = await fetchData(urlEndpoint, 'POST', payload);

    console.log('ERROR ', error);
    console.log('DATA ', data);

    if (error) {
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: error.message || i18n.global.t('creationFilters.error.generation'),
        life: 5000,
      });

      // Remove placeholder dish if generation failed
      if (tempId !== null) {
        const index = dishes.value.findIndex((d) => d.id === tempId);
        if (index !== -1) {
          dishes.value.splice(index, 1);
        }
      }

      isGenerating.value = false;
      return;
    }

    // Handle successful response with no data
    if (!data) {
      console.error('Response succeeded but data is null/undefined');
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('creationFilters.generationError'),
        life: 5000,
      });
      if (tempId) {
        dishes.value = dishes.value.filter((dish) => dish.id !== tempId);
      }
      isGenerating.value = false;
      return;
    }

    // More flexible validation - check for any valid response structure
    const hasValidDishes = data.response && Array.isArray(data.response.dishes) && data.response.dishes.length > 0;
    const hasTitleIdeas = Array.isArray(data.dish_title_ideas) && data.dish_title_ideas.length > 0;
    const hasAnyValidContent = hasValidDishes || hasTitleIdeas || data.response || data.dishes;

    if (!hasAnyValidContent) {
      console.error('Invalid response structure - no valid content found:', {
        hasValidDishes,
        hasTitleIdeas,
        hasResponse: !!data.response,
        hasDishes: !!data.dishes,
        responseKeys: Object.keys(data),
        fullData: data,
      });

      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('creationFilters.generationError'),
        life: 5000,
      });

      if (tempId) {
        dishes.value = dishes.value.filter((dish) => dish.id !== tempId);
      }
      isGenerating.value = false;
      return;
    }

    // Success! Process the response
    const date = Date.now();

    // Handle different response structures
    if (data.response?.dishes) {
      console.log('SUCCESS: Setting dishes from data.response.dishes:', data.response.dishes);
      dishes.value = data.response.dishes;
    } else if (data.dishes) {
      console.log('SUCCESS: Setting dishes from data.dishes:', data.dishes);
      dishes.value = data.dishes;
    }

    // Set response info - handle different structures
    infoTotal.value = {
      date: date,
      temperature: data.temperature,
      ...params,
      ...(data.response || {}),
      usage: data.usage,
      status: data.status,
      ai_model: data.ai_model,
      duration: data.duration,
      instructions: data.instructions,
      thread_id: data.thread_id,
    };

    // Handle other response fields
    if (data.dish_title_ideas) {
      dishTitles.value = data.dish_title_ideas;
    }
    if (data.other_dish_title_ideas) {
      otherDishTitles.value = data.other_dish_title_ideas;
    }
    if (data.other_dish_title_ideas_from_ingredients) {
      otherDishTitlesFromIngredients.value = data.other_dish_title_ideas_from_ingredients;
    }
    if (data.main_products && data.main_products !== '') {
      lastGenerationParams.value.main_product = data.main_products;
    }

    // Update GPT info
    useGptInfoDishesStore().gptInfoDishes = {
      cost: data.cost,
      status: data.status,
      thread_id: data.thread_id,
    };

    console.log('Generation completed successfully');
    isGenerating.value = false;
  }

  async function generateImages(theDishId: number, toast: ToastServiceMethods) {
    if (!useUserStore().validateUserCanGenerate()) {
      return null;
    }

    generatingImage.value = true;
    try {
      // Pour les IDs négatifs (demo), simuler une génération réussie sans appel API
      if (theDishId < 0) {
        const imageMap = {
          '-1': 'parmentier',
          '-2': 'salmonCrust',
          '-3': 'vealMeatballs',
          '-4': 'buckwheatPancake',
          '-5': 'turkeyRoast',
          '-6': 'cassoulet',
          '-7': 'tarte',
          '-8': 'coquillesSaintJacques',
          '-9': 'butternutLasagna',
          '-10': 'raviolesGratin',
          '-11': 'clementineClafoutis',
          '-12': 'whiteChocMousse',
          '-13': 'cardamomCremeBrulee',
          '-14': 'floatingIsland',
          '-15': 'hazelnutFinancier',
        };

        const imageName = imageMap[theDishId.toString()];
        if (imageName) {
          // Simuler une réponse réussie pour les plats démo
          const data = {
            response: {
              image_url_gastro: `/images/onboarding/${imageName}-gastro.webp`,
              image_url_bistro: `/images/onboarding/${imageName}-bistro.webp`,
            },
            selected_image: 'none', // Laisser l'utilisateur choisir
          };

          const selectedImage: 'user' | 'none' | 'bistro' | 'gastro' = (data.selected_image ?? selectedDish.value?.selected_image ?? 'none') as
            | 'user'
            | 'none'
            | 'bistro'
            | 'gastro';

          // Update selectedDish if it exists and matches the dish ID
          if (selectedDish.value && selectedDish.value.id === theDishId) {
            selectedDish.value.image_url_gastro = data.response.image_url_gastro;
            selectedDish.value.image_url_bistro = data.response.image_url_bistro;
            selectedDish.value.selected_image = selectedImage;
          }

          // Update dishes array if the dish exists there
          const dishIndex = dishes.value.findIndex((dish) => dish.id === theDishId);
          if (dishIndex !== -1) {
            dishes.value[dishIndex] = {
              ...dishes.value[dishIndex],
              image_url_gastro: data.response.image_url_gastro,
              image_url_bistro: data.response.image_url_bistro,
              selected_image: selectedImage,
            };
          }

          return {
            image_url_gastro: data.response.image_url_gastro,
            image_url_bistro: data.response.image_url_bistro,
            selected_image: selectedImage,
          };
        } else {
          throw new Error(`Aucune image trouvée pour le plat demo avec l'ID ${theDishId}`);
        }
      }

      // Pour les IDs positifs, utiliser l'API normale
      const { data, error } = await generateImage(theDishId);

      if (error) {
        if ((error as { code: string }).code === 'INSUFFICIENT_CREDITS') {
          toast.add({
            severity: 'error',
            summary: i18n.global.t('credits.noMoreCredit.title'),
            detail: i18n.global.t('credits.noMoreCredit.message'),
            life: 3000,
          });
        } else {
          toast.add({
            severity: 'error',
            summary: 'Error',
            detail: i18n.global.t('menuDishView.image.generating.failedToGenerate') + ' ' + error,
            life: 3000,
          });
        }
        router.go(-1);
        return null;
      }

      const selectedImage = data.selected_image ?? selectedDish.value?.selected_image ?? 'none';

      // Update selectedDish if it exists and matches the dish ID
      if (selectedDish.value && selectedDish.value.id === theDishId) {
        selectedDish.value.image_url_gastro = data.response.image_url_gastro;
        selectedDish.value.image_url_bistro = data.response.image_url_bistro;
        selectedDish.value.selected_image = selectedImage;
      }

      // Update dishes array if the dish exists there
      const dishIndex = dishes.value.findIndex((dish) => dish.id === theDishId);
      if (dishIndex !== -1) {
        dishes.value[dishIndex] = {
          ...dishes.value[dishIndex],
          image_url_gastro: data.response.image_url_gastro,
          image_url_bistro: data.response.image_url_bistro,
          selected_image: selectedImage,
        };
      }

      // Update planned menu if it exists (using the planning store)
      const planningStore = usePlanningStore();
      if (planningStore.selectedPlannedMenu) {
        const menuCourse = planningStore.selectedPlannedMenu.menu_courses.find((course) => course.dish?.id === theDishId);
        if (menuCourse?.dish) {
          menuCourse.dish.image_url_gastro = data.response.image_url_gastro;
          menuCourse.dish.image_url_bistro = data.response.image_url_bistro;
          menuCourse.dish.selected_image = selectedImage;
        }
      }

      if (planningStore.plannedMenus) {
        // Update planned menus array
        planningStore.plannedMenus.forEach((menu) => {
          menu.menu_courses.forEach((course) => {
            if (course.dish?.id === theDishId) {
              course.dish.image_url_gastro = data.response.image_url_gastro;
              course.dish.image_url_bistro = data.response.image_url_bistro;
              course.dish.selected_image = selectedImage;
            }
          });
        });
      }

      // toast.add({
      //   severity: 'success',
      //   summary: 'Success',
      //   detail: i18n.global.t('menuDishView.image.generating.success'),
      //   life: 3000,
      // });

      return {
        image_url_gastro: data.response.image_url_gastro,
        image_url_bistro: data.response.image_url_bistro,
        selected_image: selectedImage,
      };
    } catch (error) {
      console.error('Exception during image generation:', error);
      toast.add({
        severity: 'error',
        summary: 'Exception',
        detail: i18n.global.t('menuDishView.image.generating.error'),
        life: 3000,
      });
      router.go(-1);
      return null;
    } finally {
      generatingImage.value = false;
    }
  }

  async function uploadUserImage(dishId: number, file: File, toast: ToastServiceMethods) {
    const formData = new FormData();
    formData.append('imageUser', file);

    try {
      const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/images`, 'POST', formData);

      if (error) throw error;

      // Update selectedDish if it exists and matches the dish ID
      if (selectedDish.value && selectedDish.value.id === dishId) {
        selectedDish.value.image_url_user = data.updated_values.image_url_user;
        selectedDish.value.selected_image = 'user';
      }

      // Update dishes array if the dish exists there
      const dishIndex = dishes.value.findIndex((dish) => dish.id === dishId);
      if (dishIndex !== -1) {
        dishes.value[dishIndex].image_url_user = data.updated_values.image_url_user;
        dishes.value[dishIndex].selected_image = 'user';
      }

      // Update planned menu if it exists
      const planningStore = usePlanningStore();
      if (planningStore.selectedPlannedMenu) {
        const menuCourse = planningStore.selectedPlannedMenu.menu_courses.find((course) => course.dish?.id === dishId);
        if (menuCourse?.dish) {
          menuCourse.dish.image_url_user = data.updated_values.image_url_user;
          menuCourse.dish.selected_image = 'user';
        }
      }

      planningStore.plannedMenus.forEach((menu) => {
        menu.menu_courses.forEach((course) => {
          if (course.dish?.id === dishId) {
            course.dish.image_url_user = data.updated_values.image_url_user;
            course.dish.selected_image = 'user';
          }
        });
      });

      // toast.add({
      //   severity: 'success',
      //   summary: i18n.global.t('common.success'),
      //   detail: i18n.global.t('menuDishView.addUserImageSuccess'),
      //   life: 3000,
      // });

      return data.updated_values.image_url_user;
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuDishView.addUserImageError'),
        life: 3000,
      });
      return null;
    }
  }

  const fetchingTags: Ref<boolean> = ref(false);

  // Fonction pour initialiser le mode onboarding (vider les tags DB et charger les tags locaux)
  function initOnboardingMode() {
    console.log('[Onboarding] Initialisation du mode onboarding - effacement des tags DB');
    tags.value = [];

    // Charger les tags depuis localStorage s'ils existent
    const storageKey = 'onboarding:tags';
    const savedTags = localStorage.getItem(storageKey);
    if (savedTags) {
      try {
        tags.value = JSON.parse(savedTags);
        console.log('[Onboarding] Tags locaux chargés:', tags.value);
      } catch (error) {
        console.error('[Onboarding] Erreur lors du parsing des tags localStorage:', error);
        tags.value = [];
      }
    }
  }

  async function fetchTags(restaurantId, toast, isOnboarding = false) {
    // Pendant l'onboarding, charger depuis localStorage s'il y a des tags sauvegardés
    if (isOnboarding) {
      console.log('[Onboarding] Mode onboarding - chargement des tags depuis localStorage');
      fetchingTags.value = true;

      // Toujours effacer les tags DB d'abord
      tags.value = [];

      // Charger les tags depuis localStorage
      const storageKey = 'onboarding:tags';
      const savedTags = localStorage.getItem(storageKey);
      if (savedTags) {
        try {
          tags.value = JSON.parse(savedTags);
          console.log('[Onboarding] Tags chargés depuis localStorage:', tags.value);
        } catch (error) {
          console.error('[Onboarding] Erreur lors du parsing des tags localStorage:', error);
          tags.value = [];
        }
      } else {
        // Initialiser avec un tableau vide
        tags.value = [];
        console.log('[Onboarding] Aucun tag en localStorage, initialisation avec tableau vide');
      }

      fetchingTags.value = false;
      return;
    }

    try {
      fetchingTags.value = true;
      const url = `${envStore.apiUrl}/dishes/tags/${restaurantId}`;
      // if (parentId) {
      //   url += `/${parentId}/children`;
      // }
      const response = await fetchData(url);
      if (response.data) {
        tags.value = response.data as DishTag[];
      } else if (response.error) {
        toast.add({
          severity: 'error',
          summary: i18n.global.t('common.error'),
          detail: i18n.global.t('dishTag.fetch.error'),
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('dishTag.fetch.error'),
        life: 3000,
      });
    } finally {
      fetchingTags.value = false;
    }
  }

  async function updateDishTags(dishId, tags, toast) {
    // Pour les IDs négatifs (plats de démonstration), gérer localement
    if (dishId < 0) {
      console.log('[Onboarding] Mise à jour locale des tags pour ID négatif:', dishId);

      // Sauvegarder les tags dans le localStorage
      const storageKey = `onboarding:dishTags:${dishId}`;
      localStorage.setItem(storageKey, JSON.stringify(tags));

      // Mettre à jour selectedDish si c'est le bon
      if (selectedDish.value && selectedDish.value.id === dishId) {
        selectedDish.value.tags = tags;
      }

      // Mettre à jour dans le tableau des plats si présent
      const dishIndex = dishes.value.findIndex((dish) => dish.id === dishId);
      if (dishIndex !== -1) {
        dishes.value[dishIndex].tags = tags;
      }

      // Mettre à jour dans les listes de plats (historique, etc.)
      useProfileDishLists(toast).dishes.value.forEach((dish) => {
        if (dish.id === dishId) {
          dish.tags = tags;
        }
      });

      console.log('[Onboarding] Tags mis à jour localement:', tags);
      return;
    }

    const payload = {
      tags: tags,
    };

    try {
      const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/tags`, 'PUT', payload);

      if (error) {
        throw new Error('Failed to update dish tags');
      }

      // Update selectedDish if it matches
      if (selectedDish.value && selectedDish.value.id === dishId) {
        selectedDish.value.tags = data.updated_tags;
      }

      // Update dish in dishes array if it exists there
      const dishIndex = dishes.value.findIndex((dish) => dish.id === dishId);
      if (dishIndex !== -1) {
        dishes.value[dishIndex].tags = data.updated_tags;
      }

      // Update planned menu if it exists (using the planning store)
      const planningStore = usePlanningStore();
      if (planningStore.selectedPlannedMenu) {
        const menuCourse = planningStore.selectedPlannedMenu.menu_courses.find((course) => course.dish?.id === dishId);
        if (menuCourse?.dish) {
          menuCourse.dish.tags = data.updated_tags;
        }
      }

      // Update planned menus array
      planningStore.plannedMenus.forEach((menu) => {
        menu.menu_courses.forEach((course) => {
          if (course.dish?.id === dishId && course.dish) {
            course.dish.tags = data.updated_tags;
          }
        });
      });

      // update tags in the dish lists
      useProfileDishLists(toast).dishes.value.forEach((dish) => {
        if (dish.id === dishId) {
          dish.tags = data.updated_tags;
        }
      });

      // toast.add({
      //   severity: 'success',
      //   summary: i18n.global.t('common.success'),
      //   detail: i18n.global.t('menuDishView.tag.updateSuccess'),
      //   life: 3000,
      // });
    } catch (e) {
      console.error('Erreur lors de la mise à jour des tags:', e);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuDishView.tag.errorUpdatingTags'),
        life: 3000,
      });
    }
  }

  async function editTag(tag: DishTag, restaurantId: string | null, isOnboarding = false) {
    if (!tag) return;

    // Pendant l'onboarding, gérer les tags localement
    if (isOnboarding) {
      console.log('[Onboarding] Création/édition de tag en local:', tag);

      // Si c'est un nouveau tag (pas d'id ou id positif), lui assigner un ID temporaire négatif
      if (!tag.id || tag.id > 0) {
        const newId = Math.min(...(tags.value.length > 0 ? tags.value.map((t) => t.id) : [0])) - 1;
        const newTag: DishTag = {
          ...tag,
          id: newId,
          is_default: false,
          user_uuid: null,
          restaurant_uuid: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          parent_dish_tag_id: tag.parent_dish_tag_id || null,
          is_template: false,
        };
        tag = newTag;
      }

      // Ajouter ou mettre à jour le tag dans le store local (même logique que l'API)
      const tagIndex = tags.value.findIndex((t) => t.id === tag.id);
      if (tagIndex !== -1) {
        // Mise à jour d'un tag existant
        (tags.value as DishTag[])[tagIndex] = { ...tag };
      } else {
        // Nouveau tag : appliquer la même logique que l'API
        if (tag.parent_dish_tag_id) {
          // Si c'est un sous-tag, l'ajouter SEULEMENT à la liste child_tags du parent
          const parentTag = tags.value.find((t) => t.id === tag.parent_dish_tag_id);
          if (parentTag) {
            parentTag.child_tags = parentTag.child_tags || [];
            parentTag.child_tags.push({ ...tag });
            console.log('[Onboarding] Sous-tag ajouté au parent:', parentTag.name, '→', tag.name);
          } else {
            console.error('[Onboarding] Parent tag not found for child tag:', tag.name, 'parent_id:', tag.parent_dish_tag_id);
          }
        } else {
          // Si c'est un tag racine, l'ajouter au tableau principal
          (tags.value as DishTag[]).push({ ...tag });
          console.log('[Onboarding] Tag racine ajouté:', tag.name);
        }
      }

      console.log('[Onboarding] Tags après ajout/modification:', tags.value);

      // Sauvegarder dans localStorage pour persistance locale
      const storageKey = 'onboarding:tags';
      localStorage.setItem(storageKey, JSON.stringify(tags.value));

      return tag; // Retourner le tag créé/modifié
    }

    const oldTagName = tag.name;
    try {
      const payload = {
        tags: [tag],
      };
      const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/tags/${restaurantId}`, 'PATCH', payload);

      if (data) {
        // toast.add({
        //   severity: 'success',
        //   summary: i18n.global.t('common.success'),
        //   detail: i18n.global.t('dishTag.update.success'),
        //   life: 3000,
        // });
        // Update the tag in the store if needed
        const tagIndex = tags.value.findIndex((t) => t.id === tag.id);
        if (tagIndex !== -1) {
          tags.value[tagIndex] = { ...tag };
        }

        // If this is a child tag, also update it in the parent's child_tags array
        if (tag.parent_dish_tag_id) {
          const parentTag = tags.value.find((t) => t.id === tag.parent_dish_tag_id);
          if (parentTag && parentTag.child_tags) {
            const childIndex = parentTag.child_tags.findIndex((t) => t.id === tag.id);
            if (childIndex !== -1) {
              parentTag.child_tags[childIndex] = { ...tag };
            }
          }
        }
      } else if (error) {
        toast.add({
          severity: 'error',
          summary: i18n.global.t('common.error'),
          detail: error,
          life: 3000,
        });
        // Rollback to previous tag name
        tag.name = oldTagName;
      }
    } catch (error) {
      console.error('Failed to update tag:', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: 'Failed to update the tag.',
        life: 3000,
      });
      tag.name = oldTagName;
    }
  }

  // Delete tag
  async function deleteTag(tagId: number, restaurantId: string | null) {
    if (!tagId) return;

    try {
      const payload = {
        tagIds: [tagId],
      };
      const { data, error } = await fetchData(`${envStore.apiUrl}/dishes/tags/${restaurantId}`, 'DELETE', payload);

      if (data) {
        // toast.add({
        //   severity: 'success',
        //   summary: i18n.global.t('common.success'),
        //   detail: i18n.global.t('dishTag.delete.success'),
        //   life: 3000,
        // });
        // Remove the tag from the store
        tags.value = tags.value.filter((tag) => tag.id !== tagId);
      } else if (error) {
        toast.add({
          severity: 'error',
          summary: i18n.global.t('common.error'),
          detail: error,
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Failed to delete tag:', error);
      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('dishTag.delete.error'),
        life: 3000,
      });
    }
  }

  async function confirmSeeRecipe(dishId: number) {
    if (!selectedDish.value) return;
    try {
      const { error } = await fetchData(`${envStore.apiUrl}/dishes/${dishId}/recipe/confirm-see-recipe`, 'GET');
      if (error) throw error;
      selectedDish.value.needs_recipe_update = false;
    } catch (error) {
      console.error('Failed to confirm see recipe', error);
    }
  }

  async function changeSelectedImage(dishId: number, newlySelectedImage: 'user' | 'gastro' | 'bistro' | 'none') {
    if (!selectedDish.value) return;

    const previousSelectedImage = selectedDish.value.selected_image;

    // Toggle selection
    const updatedImage = selectedDish.value.selected_image === newlySelectedImage || newlySelectedImage === 'none' ? 'none' : newlySelectedImage;

    // Update selectedDish
    selectedDish.value.selected_image = updatedImage;

    // Update dish in dishes array if it exists
    const dishIndex = dishes.value.findIndex((dish) => dish.id === dishId);
    if (dishIndex !== -1) {
      dishes.value[dishIndex].selected_image = updatedImage;
    }

    // Update planned menu if it exists
    const planningStore = usePlanningStore();
    if (planningStore.selectedPlannedMenu) {
      const menuCourse = planningStore.selectedPlannedMenu.menu_courses.find((course) => course.dish?.id === dishId);
      if (menuCourse?.dish) {
        menuCourse.dish.selected_image = updatedImage;
      }
    }

    // Update dish in planned menus array
    planningStore.plannedMenus.forEach((menu) => {
      menu.menu_courses.forEach((course) => {
        if (course.dish?.id === dishId && course.dish) {
          course.dish.selected_image = updatedImage;
        }
      });
    });

    // Pour les IDs négatifs (demo), ne pas faire d'appel API
    if (dishId < 0) {
      console.log("[changeSelectedImage] ID négatif détecté, pas d'appel API");
      return true;
    }

    const { error } = await fetchData(envStore.apiUrl + '/dishes/' + dishId + '/selected-image', 'PATCH', { newSelectedImage: updatedImage });

    if (error) {
      // Revert changes in selectedDish and dishes array
      selectedDish.value.selected_image = previousSelectedImage;
      if (dishIndex !== -1) {
        dishes.value[dishIndex].selected_image = previousSelectedImage;
      }

      // Revert changes in planned menu
      if (planningStore.selectedPlannedMenu) {
        const menuCourse = planningStore.selectedPlannedMenu.menu_courses.find((course) => course.dish?.id === dishId);
        if (menuCourse?.dish) {
          menuCourse.dish.selected_image = previousSelectedImage;
        }
      }

      // Revert changes in planned menus array
      planningStore.plannedMenus.forEach((menu) => {
        menu.menu_courses.forEach((course) => {
          if (course.dish?.id === dishId && course.dish) {
            course.dish.selected_image = previousSelectedImage;
          }
        });
      });

      toast.add({
        severity: 'error',
        summary: i18n.global.t('common.error'),
        detail: i18n.global.t('menuImage.changingSelectedImageError'),
        life: 3000,
      });
      return false;
    }

    return true;
  }

  // Fonction utilitaire pour récupérer les tags sauvegardés localement pour les plats de démo
  function getLocalDishTags(dishId) {
    if (dishId >= 0) return []; // Pas pour les plats normaux

    const storageKey = `onboarding:dishTags:${dishId}`;
    const savedTags = localStorage.getItem(storageKey);

    try {
      return savedTags ? JSON.parse(savedTags) : [];
    } catch (e) {
      console.error('[Onboarding] Erreur lors du parsing des tags sauvegardés:', e);
      return [];
    }
  }

  return {
    dishes,
    selectedDish,
    generateDishes,
    generateImages,
    generatingImage,
    isGenerating,
    addIngredients,
    deleteIngredients,
    updateIngredients,
    infoTotal,
    dishTitles,
    otherDishTitles,
    otherDishTitlesFromIngredients,
    generationParams,
    lastGenerationParams,
    inspirationSource,
    resetGenerationParams,
    cleanGenerationParams,
    tags,
    fetchingTags,
    fetchTags,
    initOnboardingMode,
    updateDishTags,
    getLocalDishTags,
    editTag,
    deleteTag,
    units,
    genParamsInspirationCountry,
    genParamsInspirationRegion,
    shouldRefetch,
    showingDishFromPlan,
    generateDishFromTitle,
    generateMenuDish,
    uploadUserImage,
    confirmSeeRecipe,
    changeSelectedImage,
  };
});
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDishesStore, import.meta.hot));
}

export const useGptInfoDishesStore = defineStore('gpt-dishes', () => {
  const gptInfoDishes: Ref<{ cost: number; status: string; thread_id: string }> = ref({
    cost: 0,
    status: '',
    thread_id: '',
  });

  return { gptInfoDishes };
});

export const useDishCreationEditionFormStore = defineStore('dish-creation-edition-form', () => {
  const creationEditionDish: Ref<{
    dish_data: {
      id?: number;
      name: string;
      servings: number;
      prep_time_min: number;
      chill_time_min: number;
      cook_time_min: number;
      difficulty: number;
      ingredients: Ingredient[];
      steps: RecipeStep[];
    };
    ai_data?: AiRequest;
  } | null> = ref(null);

  return { creationEditionDish };
});

// TODO : set a type Recipe
export const useRecipeStore = defineStore('recipe', () => {
  const recipe: Ref<Recipe | null> = ref(null);
  const infoTotal: Ref<{ [key: string]: unknown } | null> = ref(null);
  return { recipe, infoTotal };
});

export const useImageStore = defineStore('image', () => {
  const imageGastro: Ref<{ [key: string]: unknown }> = ref({});
  const imageBistro: Ref<{ [key: string]: unknown }> = ref({});
  const infoTotal: Ref<{ [key: string]: unknown } | null> = ref(null);
  return { imageGastro, imageBistro, infoTotal };
});

// function parseDishData(rawJson) {
//   try {
//     // Remplacer les échappements inutiles et parse le JSON
//     const cleanedJson = rawJson.replace(/\\n/g, "").replace(/\\"/g, '"');
//     return JSON.parse(cleanedJson);
//   } catch (e) {
//     console.error("Erreur lors du parsing du JSON:", e);
//   }
// }

// // export const dishes = parseDishData("{\n  \"dishes\": [\n    {\n      \"id\": 1,\n      \"name\": \"Blanquette de la mer inspirée Asiatique\",\n      \"difficulty\": 2,\n      \"ingredients\": [\n        {\n          \"category\": \"fish\",\n          \"name\": \"Filet de cabillaud\",\n          \"quantity\": 150,\n          \"price\": 4,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Oignon\",\n          \"quantity\": 50,\n          \"price\": 0.5,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Carotte\",\n          \"quantity\": 50,\n          \"price\": 0.5,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dairy\",\n          \"name\": \"Crème fraîche\",\n          \"quantity\": 50,\n          \"price\": 1,\n          \"unit\": \"ml\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Curry\",\n          \"quantity\": 2,\n          \"price\": 0.2,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Sel\",\n          \"quantity\": 1,\n          \"price\": 0.1,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Poivre\",\n          \"quantity\": 1,\n          \"price\": 0.1,\n          \"unit\": \"g\"\n        }\n      ],\n      \"total_cost\": 3.3\n    },\n    {\n      \"id\": 2,\n      \"name\": \"Blanquette de la mer exotique\",\n      \"difficulty\": 3,\n      \"ingredients\": [\n        {\n          \"category\": \"fish\",\n          \"name\": \"Saumon\",\n          \"quantity\": 100,\n          \"price\": 5,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Gingembre\",\n          \"quantity\": 5,\n          \"price\": 0.3,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Citronnelle\",\n          \"quantity\": 5,\n          \"price\": 0.5,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Piment\",\n          \"quantity\": 1,\n          \"price\": 0.2,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dairy\",\n          \"name\": \"Lait de coco\",\n          \"quantity\": 30,\n          \"price\": 2,\n          \"unit\": \"ml\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Curcuma\",\n          \"quantity\": 1,\n          \"price\": 0.3,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Coriandre\",\n          \"quantity\": 2,\n          \"price\": 0.4,\n          \"unit\": \"g\"\n        }\n      ],\n      \"total_cost\": 3.7\n    },\n    {\n      \"id\": 3,\n      \"name\": \"Blanquette de la mer épicée\",\n      \"difficulty\": 1,\n      \"ingredients\": [\n        {\n          \"category\": \"fish\",\n          \"name\": \"Merlu\",\n          \"quantity\": 120,\n          \"price\": 3,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Ail\",\n          \"quantity\": 2,\n          \"price\": 0.3,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Poivron rouge\",\n          \"quantity\": 50,\n          \"price\": 0.5,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"fruits_veggies\",\n          \"name\": \"Tomate\",\n          \"quantity\": 50,\n          \"price\": 0.6,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dairy\",\n          \"name\": \"Yaourt nature\",\n          \"quantity\": 80,\n          \"price\": 1,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Paprika\",\n          \"quantity\": 1,\n          \"price\": 0.2,\n          \"unit\": \"g\"\n        },\n        {\n          \"category\": \"dried_economat\",\n          \"name\": \"Cumin\",\n          \"quantity\": 1,\n          \"price\": 0.3,\n          \"unit\": \"g\"\n        }\n      ],\n      \"total_cost\": 3.4\n    }\n  ]\n}");
